import { KonvaEventObject } from 'konva/lib/Node'; // Added for onClick/onTap types

export type TemplatesPageProps = Record<string, never>;

export type ElementType =
  | 'text'
  | 'barcode'
  | 'line'
  | 'box'
  | 'image'
  | 'qrcode'
  | 'table'
  | 'advancedTable'
  | 'group';

// Represents a single column selected by the user for the Advanced Table
export interface AdvancedTableColumnDefinition {
  key: string; // The data field key for this column
  headerText: string; // The text to display in the header for this column
  // Optional: suggested initial width or weight for layout calculation
  // initialWidth?: number; // e.g., 150 (pixels)
  // initialWeight?: number; // e.g., 2 (for proportional distribution)
}

// Configuration for the Advanced Table, typically from the setup modal
export interface AdvancedTableUserSetup {
  columns: AdvancedTableColumnDefinition[]; // Selected columns in display order
  // This could also include a reference to the specific dataset if needed,
  // e.g., linkedDatasetId?: string;
}

// Represents a single item in a sales order (or similar dataset for the table)
// Keys should align with DynamicField values intended for use in tables.
export interface SalesOrderItem {
  description?: string;
  discount?: string;
  item?: string;
  qtyOrdered?: string;
  tags?: string;
  totalPrice?: string;
  type?: string;
  unitPrice?: string;
  // Allow other dynamic fields as well, making it flexible
  // [key: string]: string | number | boolean | undefined;
}

export interface PageSize {
  name: string;
  width: number; // Width in pixels (96 DPI)
  height: number; // Height in pixels (96 DPI)
}

export interface AdvancedTableHeader {
  key: string; // identifier
  label: string; // display text
  path?: string; // for nested properties
}

export interface TableData {
  rows: number;
  columns: number;
  cellWidth?: number;
  cellHeight?: number;
  headerRow?: boolean;
  headerRowHeight?: number;
  headerFontSize?: number;
  headerFontFamily?: string;
  headerBackgroundColor: string;
  headerTextColor: string;
  headerTexts?: string[];
  borderColor?: string;
  borderWidth?: number;
  showGrid?: boolean;
  backgroundColor: string;
  highlightedRows?: number[];
  highlightedColumns?: number[];
  highlightColor: string;
}

export interface AdvancedTableCellData {
  content: string;
  // Future cell-specific styling can go here (e.g., individual background, text color, alignment)
}

export interface AdvancedTableData {
  numRows: number;
  numColumns: number;
  columnWidths: number[]; // Width for each column
  rowHeights: number[]; // Height for each data row
  data: (AdvancedTableCellData | null)[][]; // 2D array for cell data

  headerRow?: boolean;
  headerRowHeight?: number;
  headers?: AdvancedTableHeader[];

  // Header styling
  headerFontFamily?: string;
  headerFontSize?: number;
  headerBackgroundColor: string;
  headerTextColor: string;

  // Data cell styling defaults
  defaultCellFontFamily?: string;
  defaultCellFontSize?: number;
  defaultCellBackgroundColor?: string;
  defaultCellTextColor?: string;

  // Grid/Border styling
  borderColor?: string; // Color for cell borders and table outline
  borderWidth?: number; // Width for cell borders and table outline
  showGrid?: boolean; // Controls visibility of inner grid lines

  highlightedRows?: number[];
  highlightedColumns?: number[];
  highlightColor: string;

  width?: number; // Calculated total width of the table content
  height?: number; // Calculated total height of the table content (including header)
}

export interface AdvancedTableElementProps
  extends BaseElementProps,
    ShapeElementProps {
  advancedTableData?: AdvancedTableData;
  onUpdateElement?: (updates: Partial<LabelElement>) => void;
  onDragEnd?: (event: KonvaEventObject<DragEvent>) => void; // Added specifically for AdvancedTable
  onMouseEnter?: (event: KonvaEventObject<MouseEvent>) => void; // For cursor changes
  onMouseLeave?: (event: KonvaEventObject<MouseEvent>) => void; // For cursor changes
  previewMode?: boolean;
  // The 'ref' will be provided by React.forwardRef and does not need to be listed here.
  // onClick, onTap, draggable are inherited or part of BaseElementProps which ShapeElementProps extends
}

export interface BaseElementProps {
  id: string;
  type: ElementType;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  dynamicField?: string;
  draggable?: boolean;
  onClick?: (event: KonvaEventObject<MouseEvent | TouchEvent>) => void; // Added for selection
  onTap?: (event: KonvaEventObject<MouseEvent | TouchEvent>) => void; // Added for selection
}

export interface TextElementProps extends BaseElementProps, ShapeElementProps {
  text?: string;
  fontSize?: number;
  fontFamily?: string;
  align?: 'left' | 'center' | 'right';
  backgroundColor?: string;
  fill?: string;
}

export interface ShapeElementProps extends BaseElementProps {
  fill?: string;
  stroke?: string;
  lineWidth?: number;
}

export interface ImageElementProps extends BaseElementProps {
  src?: string;
}

export interface BarcodeElementProps extends BaseElementProps {
  barcodeType?: 'CODE128' | 'EAN13' | 'EAN8' | 'UPC' | 'CODE39';
  barcodeValue?: string;
  text?: string; // Content of the text displayed below the barcode
  fontFamily?: string; // Font family for the text
  fill?: string; // Text color (fill) for the text
}

export interface QRCodeElementProps extends BaseElementProps {
  qrValue?: string;
  fill?: string;
  website?: string;
}

export interface TableElementProps extends BaseElementProps, ShapeElementProps {
  tableData?: TableData;
  stroke?: string;
  lineWidth?: number;
}

export interface LineElementProps extends ShapeElementProps {
  type: 'line';
}

export interface GroupElement extends BaseElementProps {
  type: 'group';
  children: LabelElement[];
}

export type LabelElement =
  | TextElementProps
  | ShapeElementProps
  | ImageElementProps
  | BarcodeElementProps
  | QRCodeElementProps
  | TableElementProps
  | AdvancedTableElementProps
  | LineElementProps
  | GroupElement;

// Type guard functions to check element types
export const isTextElement = (
  element: LabelElement
): element is TextElementProps => element.type === 'text';

export const isShapeElement = (
  element: LabelElement
): element is ShapeElementProps =>
  element.type === 'box' || element.type === 'line';

export const isImageElement = (
  element: LabelElement
): element is ImageElementProps => element.type === 'image';

export const isBarcodeElement = (
  element: LabelElement
): element is BarcodeElementProps => element.type === 'barcode';

export const isQRCodeElement = (
  element: LabelElement
): element is QRCodeElementProps => element.type === 'qrcode';

export const isTableElement = (
  element: LabelElement
): element is TableElementProps => element.type === 'table';

export const isAdvancedTableElement = (
  element: LabelElement
): element is AdvancedTableElementProps => element.type === 'advancedTable';

export const isLineElement = (
  element: LabelElement
): element is LineElementProps => element.type === 'line';

export const isGroupElement = (
  element: LabelElement
): element is GroupElement => element.type === 'group';

export interface DynamicDataField {
  value: string;
  label: string;
  group: string;
}

export enum TemplateType {
  SalesOrder = 'sales order',
  Invoice = 'invoice',
  ShippingInvoice = 'shipping invoice',
  PickTicket = 'pick ticket',
  PackingList = 'packing list',
  BillOfLading = 'bill of lading',
  PurchaseOrder = 'purchase order',
  ReceivingList = 'receiving list',
  ItemBarcode = 'item barcode',
  SaleItemBarcode = 'sale item barcode',
  PurchaseOrderItemBarcode = 'purchase order item barcode',
}

export interface LegacyTemplateStructure {
  version: string;
  elements: LabelElement[];
  pageSize: PageSize;
}

export interface TemplateApiPayload {
  id: string;
  name: string;
  defaultTemplate: boolean;
  formType: string;
  template: LegacyTemplateStructure;
}
